import { Controller, Loader, IDynamicLoader } from '@tencent/workbench';
import { FORM_CONFIGS, SpinOptions } from '@framework/workbench/drawer/ui/layout/size/config';
import { Form, NumberInputFrom } from '@tencent/wb-ext-form';
import { FormItemName, IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import NoChangeAspectType from '@src/slide/domain/ui-model/element-modeles/property/lock/size-style-type';
import { HEIGHT, ISizeFeature, WIDTH } from './size-interface';
import { ImageRelativeResizeType } from '@src/slide/domain/ui-model/element-modeles/property/image-size/image-size.interface';

@Controller({ formItems: FORM_CONFIGS })
export class SizeController {
    constructor(@Loader(ISizeFeature) private loader: IDynamicLoader<ISizeFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(FormItemName.HEIGHT, 'value')
    public getHeight(): number | IMultiDiffValue {
        const heightObj = { height: this.feature.height };
        const height = this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.SIZE, heightObj);
        return this.feature.modifyShownValue(height, SpinOptions.toFixed);
    }

    @Form(FormItemName.HEIGHT, 'onChange')
    public changeHeight(value: number, from: NumberInputFrom): void {
        const { height } = this.feature.getValidValue({ height: value }, from, SpinOptions.max, SpinOptions.min);
        this.feature.handleHeightChange(height, from);
    }

    @Form(FormItemName.HEIGHT, 'options')
    public getHeightOptions() {
        return this.feature.getOptions(HEIGHT);
    }

    @Form(FormItemName.WIDTH, 'value')
    public getWidth(): number | IMultiDiffValue {
        const widthObj = { width: this.feature.width };
        const width = this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.SIZE, widthObj);
        return this.feature.modifyShownValue(width, SpinOptions.toFixed);
    }

    @Form(FormItemName.WIDTH, 'onChange')
    public changeWidth(value: number, from: NumberInputFrom): void {
        const { width } = this.feature.getValidValue({ width: value }, from, SpinOptions.max, SpinOptions.min);
        this.feature.handleWidthChange(width, from);
    }

    @Form(FormItemName.WIDTH, 'options')
    public getWidthOptions() {
        return this.feature.getOptions(WIDTH);
    }

    @Form(FormItemName.SIZE_ADJUST, 'value')
    public getLockInfo(): boolean {
        return this.getNoChangeAspect(this.feature.size?.noChangeAspectType);
    }

    @Form(FormItemName.SIZE_ADJUST, 'onChange')
    public changeSizeAdjust(value: boolean): void {
        this.feature.handleLocksChange(value);
    }

    // @ts-ignore
    @Form(FormItemName.SIZE_ADJUST, 'options')
    public getOptions() {
        return {
            fillType: this.getFillType(this.feature.size?.noChangeAspectType),
        };
    }

    @Form(FormItemName.SCALE_HEIGHT, 'value')
    public getScaleHeight(): number | IMultiDiffValue {
        const scaleHeightObj = { scaleHeight: this.feature.scaleHeight };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.SIZE, scaleHeightObj);
    }

    @Form(FormItemName.SCALE_HEIGHT, 'onChange')
    public changeScaleHeight(value: number, from: NumberInputFrom): void {
        const options = this.feature.getElementSizeScaleOptions(HEIGHT);
        const { scaleHeight } = this.feature.getValidValue(
            { scaleHeight: value }, from,
            options.max, options.min,
        );
        this.feature.handleElementSizeScaleChange(scaleHeight, HEIGHT, from);
    }

    @Form(FormItemName.SCALE_HEIGHT, 'options')
    public getScaleHeightOptions() {
        return this.feature.getElementSizeScaleOptions(HEIGHT);
    }

    @Form(FormItemName.SCALE_WIDTH, 'value')
    public getScaleWidth(): number | IMultiDiffValue {
        const scaleWidthObj = { scaleWidth: this.feature.scaleWidth };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.SIZE, scaleWidthObj);
    }

    @Form(FormItemName.SCALE_WIDTH, 'onChange')
    public changeScaleWidth(value: number, from: NumberInputFrom): void {
        const options = this.feature.getElementSizeScaleOptions(WIDTH);
        const { scaleWidth } = this.feature.getValidValue(
            { scaleWidth: value }, from,
            options.max, options.min,
        );
        this.feature.handleElementSizeScaleChange(scaleWidth, WIDTH, from);
    }

    @Form(FormItemName.SCALE_WIDTH, 'options')
    public getScaleWidthOptions() {
        return this.feature.getElementSizeScaleOptions(WIDTH);
    }

    @Form(FormItemName.SCALE_WIDTH, 'shouldDisabled')
    @Form(FormItemName.SCALE_HEIGHT, 'shouldDisabled')
    public scaleScaleDisabled(): boolean {
        return this.feature.getSelectModel().isTextAttachment?.()
        || this.feature.height === 0
        || this.feature.width === 0;
    }

    @Form(FormItemName.ORIGINAL_SIZE_CONTENT, 'value')
    public getOriginalSizeContent(): string {
        return this.feature.getOriginalSizeContent();
    }

    @Form(FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, 'value')
    public getRelativeImageOriginalSize(): boolean {
        return !!this.feature.getRelativeImageOriginalSize();
    }

    @Form(FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, 'onChange')
    public changeRelativeImageOriginalSize(value: boolean): void {
        this.feature.handleRelativeImageOriginalSize(value);
    }

    // @ts-ignore
    @Form(FormItemName.RELATIVE_IMAGE_ORIGINAL_SIZE, 'options')
    public getImageSizeOptions() {
        const fillType = this.feature.getRelativeImageOriginalSize() === ImageRelativeResizeType.multi
            ? FillType : undefined;
        return { fillType };
    }

    @Form(FormItemName.BEST_PLAY_SCALE, 'value')
    public getBestPlayScale(): boolean {
        return this.feature.isBestPlayScale;
    }

    @Form(FormItemName.BEST_PLAY_SCALE, 'onChange')
    public changeBestPlayScale(value: boolean): void {
        this.feature.handleBestPlayScale(value);
    }

    @Form(FormItemName.RESOLUTION, 'value')
    public getResolution(): string {
        return this.feature.resolution;
    }

    @Form(FormItemName.RESOLUTION, 'onChange')
    public changeResolution(value: string): void {
        this.feature.handleResolution(value);
    }

    @Form(FormItemName.RESOLUTION, 'shouldDisabled')
    @Form(FormItemName.RESOLUTION_TEXT, 'shouldDisabled')
    public resolutionDisabled(): boolean {
        return !this.feature.isBestPlayScale;
    }

    @Form(FormItemName.RESET_ORIGINAL_SIZE, 'onClick')
    public handleResetOriginalSize(): void {
        this.feature.handleResetOriginalSize();
    }

    @Form(FormItemName.RESET_ORIGINAL_SIZE, 'shouldDisabled')
    public resetShouldDisabled(): boolean {
        return this.feature.shouldResetDisabled;
    }

    // 获取锁定纵横比的类型，是锁定，未锁定，还是多选态
    private getNoChangeAspect = noChangeAspectType => noChangeAspectType === NoChangeAspectType.NO_CHANGE_ASPECT
        || noChangeAspectType === NoChangeAspectType.MULTI;

    // 获取选中框的填充类型
    private getFillType = noChangeAspectType => (noChangeAspectType === NoChangeAspectType.MULTI
        ? FillType : undefined);
}
